import { applyI18n, t } from '../i18n';
import { filterPackets, presentProtocolFilters, type PcapFilters, type PcapProtocolFilter } from '../files/pcapFilter';
import {
  formatHexDump,
  parseCapture,
  type CapturePreview,
  type PacketSummary,
} from '../files/pcapParse';
import { openPcapStreamDialog } from './pcapStreamDialog';

export async function renderPcapPreview(content: HTMLElement, blob: Blob): Promise<boolean> {
  try {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const capture = parseCapture(bytes);
    if (!capture) return false;
    renderCapture(content, capture);
    return true;
  } catch {
    return false;
  }
}

function renderCapture(content: HTMLElement, capture: CapturePreview): void {
  const wrap = document.createElement('div');
  wrap.className = 'file-preview-pcap';

  const summary = document.createElement('div');
  summary.className = 'file-preview-pcap-summary';
  const summaryBits = [
    t(capture.format === 'pcapng' ? 'files.preview.pcap.formatPcapng' : 'files.preview.pcap.formatPcap'),
    t('files.preview.pcap.linkType', { type: linkTypeLabel(capture.linkType) }),
    t('files.preview.pcap.packetCount', { count: capture.packetCount }),
  ];
  if (capture.firstTs != null && capture.lastTs != null) {
    summaryBits.push(
      t('files.preview.pcap.timeRange', {
        from: formatTimestamp(capture.firstTs),
        to: formatTimestamp(capture.lastTs),
      }),
    );
  }
  summary.textContent = summaryBits.join(' · ');
  wrap.append(summary);

  if (capture.truncated) {
    const truncated = document.createElement('p');
    truncated.className = 'muted file-preview-pcap-truncated';
    truncated.textContent = t('files.preview.pcap.truncated', { count: capture.packetCount });
    wrap.append(truncated);
  }

  const filters = document.createElement('form');
  filters.className = 'file-preview-pcap-filters';
  filters.addEventListener('submit', (event) => event.preventDefault());
  const protocolOptions = presentProtocolFilters(capture.packets)
    .map((value) => `<option value="${value}" data-i18n="files.preview.pcap.filter.${value}"></option>`)
    .join('');
  filters.innerHTML = `
    <label>
      <span data-i18n="files.preview.pcap.filter.protocol"></span>
      <select id="pcap-filter-protocol">${protocolOptions}</select>
    </label>
    <label>
      <span data-i18n="files.preview.pcap.filter.address"></span>
      <input id="pcap-filter-address" type="text" autocomplete="off" spellcheck="false" />
    </label>
    <label>
      <span data-i18n="files.preview.pcap.filter.port"></span>
      <input id="pcap-filter-port" type="text" inputmode="numeric" autocomplete="off" />
    </label>
    <button type="button" class="button ghost" id="pcap-stream-follow" data-i18n="files.preview.pcap.stream.follow" disabled></button>
    <button type="button" class="button ghost" id="pcap-stream-hide" data-i18n="files.preview.pcap.stream.hide" disabled></button>
    <button type="button" class="button ghost" id="pcap-stream-conversation" data-i18n="files.preview.pcap.stream.conversation" disabled></button>
    <button type="button" class="button ghost" id="pcap-filter-clear" data-i18n="files.preview.pcap.filter.clear"></button>
  `;
  applyI18n(filters);
  wrap.append(filters);

  const streamChip = document.createElement('div');
  streamChip.className = 'file-preview-pcap-stream-chip';
  streamChip.id = 'pcap-filter-stream-chip';
  wrap.append(streamChip);

  const status = document.createElement('p');
  status.className = 'muted file-preview-pcap-status';
  status.id = 'pcap-filter-status';
  wrap.append(status);

  const split = document.createElement('div');
  split.className = 'file-preview-pcap-split';

  const tableWrap = document.createElement('div');
  tableWrap.className = 'file-preview-table-wrap file-preview-pcap-table-wrap';
  const table = document.createElement('table');
  table.className = 'data-table file-preview-table file-preview-pcap-table';
  const thead = document.createElement('thead');
  thead.innerHTML = `<tr>
    <th data-i18n="files.preview.pcap.col.no"></th>
    <th data-i18n="files.preview.pcap.col.time"></th>
    <th data-i18n="files.preview.pcap.col.source"></th>
    <th data-i18n="files.preview.pcap.col.destination"></th>
    <th data-i18n="files.preview.pcap.col.protocol"></th>
    <th data-i18n="files.preview.pcap.col.length"></th>
    <th data-i18n="files.preview.pcap.col.info"></th>
  </tr>`;
  applyI18n(thead);
  table.append(thead);
  const tbody = document.createElement('tbody');
  tbody.id = 'pcap-packet-body';
  table.append(tbody);
  tableWrap.append(table);

  const detail = document.createElement('div');
  detail.className = 'file-preview-pcap-detail';
  const fieldsEl = document.createElement('dl');
  fieldsEl.className = 'file-preview-pcap-fields';
  fieldsEl.id = 'pcap-packet-fields';
  const hexWrap = document.createElement('div');
  hexWrap.className = 'file-preview-pcap-hex-wrap';
  const hexHeader = document.createElement('div');
  hexHeader.className = 'file-preview-pcap-hex-header';

  const hexTitle = document.createElement('span');
  hexTitle.setAttribute('data-i18n', 'files.preview.pcap.hex');

  const hexModeSelect = document.createElement('select');
  hexModeSelect.id = 'pcap-hex-mode';
  hexModeSelect.innerHTML = `
    <option value="payload" data-i18n="files.preview.pcap.hex.payload"></option>
    <option value="frame" data-i18n="files.preview.pcap.hex.frame"></option>
  `;
  hexHeader.append(hexTitle, hexModeSelect);
  const hexEl = document.createElement('pre');
  hexEl.className = 'file-preview-pcap-hex';
  hexEl.id = 'pcap-packet-hex';
  hexWrap.append(hexHeader, hexEl);
  detail.append(fieldsEl, hexWrap);

  const gutter = document.createElement('button');
  gutter.type = 'button';
  gutter.className = 'file-preview-pcap-gutter';
  gutter.setAttribute('aria-label', t('files.preview.pcap.resizeGutter'));

  split.append(tableWrap, gutter, detail);
  wrap.append(split);
  content.append(wrap);
  bindSplitResize(split, gutter);

  const protocolSelect = filters.querySelector('#pcap-filter-protocol') as HTMLSelectElement;
  const addressInput = filters.querySelector('#pcap-filter-address') as HTMLInputElement;
  const portInput = filters.querySelector('#pcap-filter-port') as HTMLInputElement;
  const clearButton = filters.querySelector('#pcap-filter-clear') as HTMLButtonElement;
  const followStreamButton = filters.querySelector('#pcap-stream-follow') as HTMLButtonElement;
  const hideStreamButton = filters.querySelector('#pcap-stream-hide') as HTMLButtonElement;
  const conversationButton = filters.querySelector('#pcap-stream-conversation') as HTMLButtonElement;

  applyI18n(hexWrap);

  let selectedIndex: number | null = capture.packets[0]?.index ?? null;
  let activePacket: PacketSummary | null = null;
  let followedStreamId: number | null = null;
  let hiddenStreamIds: number[] = [];

  let hexMode: 'payload' | 'frame' = 'payload';

  const readFilters = (): PcapFilters => ({
    protocol: (protocolSelect.value || 'all') as PcapProtocolFilter,
    address: addressInput.value,
    port: portInput.value,
    streamId: followedStreamId,
    hiddenStreamIds,
  });

  const refresh = () => {
    const visible = filterPackets(capture.packets, readFilters());
    const stillVisible = visible.find((packet) => packet.index === selectedIndex) ?? visible[0] ?? null;
    selectedIndex = stillVisible?.index ?? null;
    activePacket = stillVisible;

    const streamEnabled = stillVisible?.streamId != null
      && (stillVisible.protocol === 'TCP' || stillVisible.protocol === 'UDP');
    followStreamButton.disabled = !streamEnabled;
    hideStreamButton.disabled = !streamEnabled;
    conversationButton.disabled = !streamEnabled;

    const chipText = stillVisible?.streamId != null
      ? followedStreamId != null
        ? t('files.preview.pcap.stream.chip.follow', { id: followedStreamId })
        : hiddenStreamIds.length > 0
          ? t('files.preview.pcap.stream.chip.hide', { id: hiddenStreamIds[0] })
          : ''
      : (followedStreamId != null
        ? t('files.preview.pcap.stream.chip.follow', { id: followedStreamId })
        : hiddenStreamIds.length > 0
          ? t('files.preview.pcap.stream.chip.hide', { id: hiddenStreamIds[0] })
          : '');
    streamChip.textContent = chipText;
    streamChip.style.display = chipText ? '' : 'none';

    renderRows(tbody, visible, selectedIndex, (packet) => {
      selectedIndex = packet.index;
      refresh();
    });
    renderDetail(fieldsEl, hexEl, stillVisible, hexMode);
    hexMode = hexModeSelect.value === 'payload' ? 'payload' : 'frame';
    status.textContent = visible.length === 0
      ? t('files.preview.pcap.empty')
      : t('files.preview.pcap.shown', { shown: visible.length, parsed: capture.packetCount });
  };

  protocolSelect.addEventListener('change', refresh);
  addressInput.addEventListener('input', refresh);
  portInput.addEventListener('input', refresh);
  clearButton.addEventListener('click', () => {
    protocolSelect.value = 'all';
    addressInput.value = '';
    portInput.value = '';
    followedStreamId = null;
    hiddenStreamIds = [];
    refresh();
  });

  hexModeSelect.addEventListener('change', () => {
    hexMode = (hexModeSelect.value === 'payload' ? 'payload' : 'frame');
    renderDetail(fieldsEl, hexEl, activePacket, hexMode);
    hexMode = hexModeSelect.value === 'payload' ? 'payload' : 'frame';
  });

  followStreamButton.addEventListener('click', () => {
    if (!activePacket?.streamId || activePacket.streamId < 0) return;
    followedStreamId = activePacket.streamId;
    hiddenStreamIds = [];
    refresh();
  });

  hideStreamButton.addEventListener('click', () => {
    if (!activePacket?.streamId || activePacket.streamId < 0) return;
    followedStreamId = null;
    hiddenStreamIds = [activePacket.streamId];
    refresh();
  });

  conversationButton.addEventListener('click', () => {
    if (activePacket?.streamId == null || activePacket.streamId < 0) return;
    openPcapStreamDialog(capture.packets, activePacket.streamId);
  });
  refresh();
}

function bindSplitResize(split: HTMLElement, gutter: HTMLElement): void {
  const minListPx = 80;
  const minDetailPx = 96;

  const applyDetailHeight = (clientY: number) => {
    const rect = split.getBoundingClientRect();
    const gutterPx = Math.max(gutter.getBoundingClientRect().height, 6);
    const available = rect.height - gutterPx;
    if (available <= minListPx + minDetailPx) {
      return;
    }
    const detailPx = Math.min(Math.max(rect.bottom - clientY, minDetailPx), available - minListPx);
    split.style.gridTemplateRows = `minmax(${minListPx}px, 1fr) ${gutterPx}px ${detailPx}px`;
  };

  gutter.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    gutter.setPointerCapture(event.pointerId);
    split.classList.add('file-preview-pcap-split--dragging');
    applyDetailHeight(event.clientY);
  });
  gutter.addEventListener('pointermove', (event) => {
    if (!gutter.hasPointerCapture(event.pointerId)) {
      return;
    }
    applyDetailHeight(event.clientY);
  });
  const stopDrag = (event: PointerEvent) => {
    if (gutter.hasPointerCapture(event.pointerId)) {
      gutter.releasePointerCapture(event.pointerId);
    }
    split.classList.remove('file-preview-pcap-split--dragging');
  };
  gutter.addEventListener('pointerup', stopDrag);
  gutter.addEventListener('pointercancel', stopDrag);
}

function renderRows(
  tbody: HTMLElement,
  packets: PacketSummary[],
  selectedIndex: number | null,
  onSelect: (packet: PacketSummary) => void,
): void {
  tbody.replaceChildren();
  for (const packet of packets) {
    const tr = document.createElement('tr');
    tr.dataset.packetIndex = String(packet.index);
    if (packet.index === selectedIndex) {
      tr.setAttribute('aria-selected', 'true');
    }
    appendTextCell(tr, String(packet.index));
    appendTextCell(tr, formatTimestamp(packet.timestampMs));
    appendTextCell(tr, packet.src);
    appendTextCell(tr, packet.dst);
    appendTextCell(tr, packet.protocol);
    appendTextCell(tr, String(packet.length));
    appendTextCell(tr, packet.info);
    tr.addEventListener('click', () => onSelect(packet));
    tbody.append(tr);
  }
}

function renderDetail(
  fieldsEl: HTMLElement,
  hexEl: HTMLElement,
  packet: PacketSummary | null,
  hexMode: 'payload' | 'frame',
): void {
  fieldsEl.replaceChildren();
  if (!packet) {
    hexEl.textContent = '';
    return;
  }
  for (const field of packet.fields) {
    const dt = document.createElement('dt');
    dt.textContent = field.label;
    const dd = document.createElement('dd');
    dd.textContent = field.value;
    fieldsEl.append(dt, dd);
  }

  const showPayload = hexMode === 'payload'
    && packet.payloadOffset != null
    && packet.payloadLength != null
    && packet.payloadLength > 0;

  const bytesToShow = hexMode === 'payload' && showPayload
    ? packet.bytes.subarray(packet.payloadOffset!, packet.payloadOffset! + packet.payloadLength!)
    : hexMode === 'frame'
      ? packet.bytes
      : new Uint8Array();
  hexEl.textContent = formatHexDump(bytesToShow);
}

function appendTextCell(row: HTMLElement, value: string): void {
  const td = document.createElement('td');
  td.textContent = value;
  row.append(td);
}

function formatTimestamp(ms: number): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return String(ms);
  return date.toISOString();
}

function linkTypeLabel(linkType: number): string {
  switch (linkType) {
    case 1:
      return t('files.preview.pcap.link.ethernet');
    case 113:
      return t('files.preview.pcap.link.linuxSll');
    case 12:
    case 14:
      return t('files.preview.pcap.link.rawIp');
    default:
      return t('files.preview.pcap.link.unknown', { type: linkType });
  }
}
